import React from "react";
import sanitizeHtml from "sanitize-html";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { Iconography } from "@hid-galaxy-ui/galaxy-react";

type ActivityDescriptionModalProps = {
  open: boolean;
  onClose: () => void;
  activityName?: string;
  description?: string;
};

const decodeHtmlEntities = (value: string): string => {
  if (typeof document === "undefined") return value;
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    const ta = document.createElement("textarea");
    ta.innerHTML = current;
    const decoded = ta.value;
    if (decoded === current) break;
    current = decoded;
  }
  return current;
};

const toSanitizedRichHtml = (content?: string): string => {
  if (!content) return "";
  const hasHtmlEntities = /&(lt|gt|amp|quot|nbsp|#\d+|#x[0-9a-f]+);/i.test(content);
  const decoded = hasHtmlEntities ? decodeHtmlEntities(content) : content;
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(decoded);

  if (!hasHtmlTags) {
    const safeText = sanitizeHtml(decoded, { allowedTags: [], allowedAttributes: {} });
    return safeText.replace(/\r\n|\r|\n/g, "<br />").trim();
  }

  return sanitizeHtml(decoded, {
    allowedTags: [
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "a",
    ],
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    transformTags: {
      a: sanitizeHtml.simpleTransform("a", {
        target: "_blank",
        rel: "noopener noreferrer",
      }),
    },
  }).trim();
};

const ActivityDescriptionModal: React.FC<ActivityDescriptionModalProps> = ({
  open,
  onClose,
  activityName,
  description,
}) => {
  const sanitizedDescription = toSanitizedRichHtml(description);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "26px",
              lineHeight: "30px",
              letterSpacing: "0.2px",
              color: "#222222",
              mt: 1.5,
            }}
          >
            {activityName || "Activity"}
          </Typography>
        </Box>

        <IconButton aria-label="Close" onClick={onClose} sx={{ mt: 0.5 }}>
          <Iconography icon="xmark" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2, pt: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Description
          </Typography>
          <Box
            sx={{ color: "text.secondary", lineHeight: 1.7, fontSize: "0.875rem" }}
            dangerouslySetInnerHTML={{
              __html: sanitizedDescription || "<p>No description available.</p>",
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDescriptionModal;
